import { useState, useEffect, useRef, useCallback } from "react"
import type { Team } from "../types"

// Cache for loaded teams to avoid re-fetching
let teamsCache: Team[] | null = null

// Force cache clear for development - remove in production
if (process.env.NODE_ENV === 'development') {
  teamsCache = null
  console.log('Teams cache cleared for development')
}

// Clear cache function for development/debugging
export function clearTeamsCache() {
  teamsCache = null
}

// Load teams from JSON files with caching
async function loadTeamsFromJSON(): Promise<Team[]> {
  // Return cached data if available
  if (teamsCache) {
    return teamsCache
  }

  try {
    // Fetch all JSON files in parallel for maximum speed
    const [nflRes, mlbRes, nbaRes, nhlRes, mlsRes, nationalRes, soccerRes, nwslRes, wnbaRes, f1Res, ncaaRes] = await Promise.all([
      fetch('/enhanced_nfl_teams.json'),
      fetch('/enhanced_mlb_teams.json'), 
      fetch('/enhanced_nba_teams.json'),
      fetch('/enhanced_nhl_teams.json'),
      fetch('/enhanced_mls_teams.json'),
      fetch('/enhanced_national_teams.json'),
      fetch('/enhanced_top_tier_soccer_leagues.json'),
      fetch('/enhanced_nwsl_teams.json'),
      fetch('/enhanced_wnba_teams.json'),
      fetch(`/formula_1_teams.json?t=${Date.now()}`).then(res => {
        console.log('F1 fetch response:', res.status, res.ok);
        return res;
      }),
      fetch('/ncaa_d1_conferences_merged.json')
    ])
    

    // Parse all JSON responses in parallel
    const [nflData, mlbData, nbaData, nhlData, mlsData, nationalData, soccerData, nwslData, wnbaData, f1Data, ncaaData] = await Promise.all([
      nflRes.json(),
      mlbRes.json(),
      nbaRes.json(), 
      nhlRes.json(),
      mlsRes.json(),
      nationalRes.json(),
      soccerRes.json(),
      nwslRes.json(),
      wnbaRes.json(),
      f1Res.json(),
      ncaaRes.json()
    ])

    // Map data to consistent Team format - process all leagues simultaneously
    const nflTeams = mapNFLTeams(nflData)
    const mlbTeams = mapMLBTeams(mlbData)
    const nbaTeams = mapNBATeams(nbaData)
    const nhlTeams = mapNHLTeams(nhlData)
    const mlsTeams = mapMLSTeams(mlsData)
    const nationalTeams = mapNationalTeams(nationalData)
    const soccerTeams = mapSoccerLeagues(soccerData)
    const nwslTeams = mapNWSLTeams(nwslData)
    const wnbaTeams = mapWNBATeams(wnbaData)
    const f1Teams = mapF1Teams(f1Data)
    const ncaaTeams = mapNCAATeams(ncaaData)

    console.log('Team counts by league:', {
      NFL: nflTeams.length,
      MLB: mlbTeams.length,
      NBA: nbaTeams.length,
      NHL: nhlTeams.length,
      MLS: mlsTeams.length,
      National: nationalTeams.length,
      Soccer: soccerTeams.length,
      NWSL: nwslTeams.length,
      WNBA: wnbaTeams.length,
      F1: f1Teams.length,
      NCAA: ncaaTeams.length
    })
    

    const allTeams: Team[] = [
      ...nflTeams,
      ...mlbTeams,
      ...nbaTeams,
      ...nhlTeams,
      ...mlsTeams,
      ...nationalTeams,
      ...soccerTeams,
      ...nwslTeams,
      ...wnbaTeams,
      ...f1Teams,
      ...ncaaTeams
    ]

    // Cache the results for future use
    teamsCache = allTeams
    console.log(`Loaded and cached ${allTeams.length} teams from JSON files`)
    console.log('Total teams by sport:', allTeams.reduce((acc, team) => {
      acc[team.sport] = (acc[team.sport] || 0) + 1;
      return acc;
    }, {} as Record<string, number>))
    
    
    return allTeams
  } catch (error) {
    console.error('Error loading teams from JSON:', error)
    return []
  }
}

// Team mapping functions (similar to server-side)
function mapNFLTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NFL",
    sport: "Football", 
    conference: team.conference,
    division: team.division,
    founded: team.founded || 1920,
    information: team.information,
    state: team.state || "",
    country: "USA",
    primaryColor: team.primary_color || "#013369",
    secondaryColor: team.secondary_color || "#D50A0A",
  }))
}

function mapMLBTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city || team.locationName,
    abbreviation: team.abbreviation,
    league: "MLB",
    sport: "Baseball",
    conference: team.league || "",
    division: team.division || "", 
    founded: team.founded || (team.firstYearOfPlay ? Number(team.firstYearOfPlay) : 1900),
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#002D72",
    secondaryColor: team.secondary_color || "#E4002C",
  }))
}

function mapNBATeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NBA", 
    sport: "Basketball",
    conference: team.conference,
    division: team.division,
    founded: team.founded || 1950,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#17408B",
    secondaryColor: team.secondary_color || "#C9082A",
  }))
}

function mapNHLTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "NHL",
    sport: "Hockey",
    conference: team.conference,
    division: team.division, 
    founded: team.founded || 1917,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#000000",
    secondaryColor: team.secondary_color || "#FFFFFF",
  }))
}

function mapMLSTeams(data: any[]): Team[] {
  return data.map(team => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "MLS",
    sport: "Soccer",
    conference: team.conference,
    division: team.division || "",
    founded: team.founded || 1996,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA", 
    primaryColor: team.primary_color || "#1D4289",
    secondaryColor: team.secondary_color || "#52B2E8",
  }))
}

function mapNationalTeams(data: any): Team[] {
  const allTeams: Team[] = []
  
  // Map all confederations
  const confederations = ['uefa', 'afc', 'caf', 'concacaf', 'conmebol', 'ofc']
  
  confederations.forEach(confederation => {
    if (data[confederation] && Array.isArray(data[confederation])) {
      const teams = data[confederation].map((team: any) => ({
        id: team.id,
        name: team.name,
        city: team.name,
        abbreviation: team.abbreviation,
        league: confederation.toUpperCase(),
        sport: "Soccer",
        conference: confederation.toUpperCase(),
        division: "",
        founded: 1954, // FIFA founded in 1904, but modern confederations around 1954
        information: team.information,
        state: "",
        country: team.name,
        primaryColor: team.primary_color || "#003366",
        secondaryColor: team.secondary_color || "#FFD700",
      }))
      allTeams.push(...teams)
    }
  })
  
  return allTeams
}

function mapSoccerLeagues(data: any): Team[] {
  const allTeams: Team[] = []
  
  // Handle array structure where each league is an object with teams array
  if (Array.isArray(data)) {
    data.forEach((league: any) => {
      if (league.teams && Array.isArray(league.teams)) {
        const teams = league.teams.map((team: any) => ({
          id: team.id,
          name: team.name,
          city: team.city,
          abbreviation: team.abbreviation,
          league: league.league || "Unknown League",
          sport: "Soccer",
          conference: league.country || "",
          division: "",
          founded: team.founded || 1900,
          information: team.information,
          state: "",
          country: league.country || "",
          primaryColor: team.primary_color || "#37003C",
          secondaryColor: team.secondary_color || "#00FF87",
        }))
        allTeams.push(...teams)
      }
    })
  }
  // Handle legacy object structure (fallback)
  else if (typeof data === 'object') {
    if (data.premier_league?.teams) {
      const teams = data.premier_league.teams.map((team: any) => ({
        id: team.id,
        name: team.name,
        city: team.city,
        abbreviation: team.abbreviation,
        league: "Premier League",
        sport: "Soccer",
        conference: "England",
        division: "",
        founded: team.founded || 1992,
        information: team.information,
        state: "",
        country: "England",
        primaryColor: team.primary_color || "#37003C",
        secondaryColor: team.secondary_color || "#00FF87",
      }))
      allTeams.push(...teams)
    }
    
    if (data.la_liga?.teams) {
      const teams = data.la_liga.teams.map((team: any) => ({
        id: team.id,
        name: team.name,
        city: team.city,
        abbreviation: team.abbreviation,
        league: "La Liga",
        sport: "Soccer",
        conference: "Spain",
        division: "",
        founded: team.founded || 1929,
        information: team.information,
        state: "",
        country: "Spain",
        primaryColor: team.primary_color || "#FF6900",
        secondaryColor: team.secondary_color || "#FFFFFF",
      }))
      allTeams.push(...teams)
    }
  }
  
  return allTeams
}

function mapNWSLTeams(data: any[]): Team[] {
  if (!data || !Array.isArray(data)) return []
  
  return data.flatMap(league => {
    if (!league.teams || !Array.isArray(league.teams)) return []
    
    return league.teams.map((team: any) => ({
      id: team.id,
      name: team.name,
      city: team.city,
      abbreviation: team.abbreviation,
      league: "NWSL",
      sport: "Soccer",
      conference: "",
      division: "",
      founded: team.founded || 2009,
      information: team.information,
      state: "",
      country: "USA",
      primaryColor: team.primary_color || "#FF6B6B",
      secondaryColor: team.secondary_color || "#000000",
    }))
  })
}

function mapWNBATeams(data: any): Team[] {
  if (!data || !data.teams || !Array.isArray(data.teams)) return []
  
  return data.teams.map((team: any) => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "WNBA",
    sport: "Basketball",
    conference: team.conference || "",
    division: team.division || "",
    founded: team.founded || 1997,
    information: team.information,
    state: team.state || "",
    country: team.country || "USA",
    primaryColor: team.primary_color || "#C8102E",
    secondaryColor: team.secondary_color || "#1D428A",
  }))
}

function mapF1Teams(data: any): Team[] {
  console.log('mapF1Teams called with data:', data);
  
  if (!data || !data.teams || !Array.isArray(data.teams)) {
    console.log('F1 data validation failed:', { hasData: !!data, hasTeams: !!data?.teams, isArray: Array.isArray(data?.teams) });
    return []
  }
  
  const mappedTeams = data.teams.map((team: any) => ({
    id: team.id,
    name: team.name,
    city: team.city,
    abbreviation: team.abbreviation,
    league: "Formula 1",
    sport: "Racing",
    conference: "Global",
    division: "",
    founded: team.founded || 1950,
    information: team.information,
    state: "",
    country: "Global",
    primaryColor: team.primary_color || "#E10600",
    secondaryColor: team.secondary_color || "#FFFFFF",
  }))
  
  console.log('F1 teams mapped successfully:', mappedTeams.length, 'teams');
  console.log('First F1 team example:', mappedTeams[0]);
  return mappedTeams;
}

function mapNCAATeams(data: any): Team[] {
  const allTeams: Team[] = []
  const seenIds = new Set<string>()
  
  if (!data || !data.conferences || !Array.isArray(data.conferences)) return []
  
  data.conferences.forEach((conference: any) => {
    if (conference.schools && Array.isArray(conference.schools)) {
      conference.schools.forEach((school: any) => {
        // Skip if we've already seen this team ID
        if (seenIds.has(school.id)) {
          console.log(`Skipping duplicate team ID: ${school.id} (${school.name})`)
          return
        }
        
        seenIds.add(school.id)
        allTeams.push({
          id: school.id,
          name: school.name,
          city: school.city,
          abbreviation: school.abbreviation,
          league: "NCAA Division I",
          sport: "College",
          conference: conference.name || conference.abbreviation || "Unknown Conference",
          division: "",
          founded: school.founded || 1900,
          information: school.information,
          state: school.state || "",
          country: "USA",
          primaryColor: school.primary_color || "#003366",
          secondaryColor: school.secondary_color || "#FFD700",
        })
      })
    }
  })
  
  return allTeams
}

type TeamsParams = {
  search?: string
  league?: string
}

type SortOption = 'name' | 'city' | 'league' | 'sport' | 'founded'
type SortDirection = 'asc' | 'desc'

type PaginationOptions = {
  page: number
  pageSize: 25 | 50 | 100
}

export function useTeams() {
  const [teams, setTeams] = useState<Team[]>([])
  const [paginatedTeams, setPaginatedTeams] = useState<Team[]>([])
  const [allTeams, setAllTeams] = useState<Team[]>([]) // Keep all teams for client-side filtering
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeSport, setActiveSport] = useState<string | null>(null)
  const [activeLeagues, setActiveLeagues] = useState<string[]>([])
  const [availableSports, setAvailableSports] = useState<string[]>([])
  const [availableLeagues, setAvailableLeagues] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  
  // Sorting state
  const [sortBy, setSortBy] = useState<SortOption>('name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState<25 | 50 | 100>(50)
  const [totalTeams, setTotalTeams] = useState(0)
  
  const searchTimeoutRef = useRef<number | null>(null)

  /**
   * Sort teams based on the current sort option and direction
   */
  const sortTeams = useCallback((teamsToSort: Team[]): Team[] => {
    return [...teamsToSort].sort((a, b) => {
      let valueA: any, valueB: any
      
      switch (sortBy) {
        case 'name':
          valueA = (a.name || '').toLowerCase()
          valueB = (b.name || '').toLowerCase()
          break
        case 'city':
          valueA = (a.city || '').toLowerCase()
          valueB = (b.city || '').toLowerCase()
          break
        case 'league':
          valueA = (a.league || '').toLowerCase()
          valueB = (b.league || '').toLowerCase()
          break
        case 'sport':
          valueA = (a.sport || '').toLowerCase()
          valueB = (b.sport || '').toLowerCase()
          break
        case 'founded':
          valueA = a.founded || 0
          valueB = b.founded || 0
          break
        default:
          valueA = (a.name || '').toLowerCase()
          valueB = (b.name || '').toLowerCase()
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [sortBy, sortDirection])

  /**
   * Paginate teams based on current page and page size
   */
  const paginateTeams = useCallback((teamsToPaginate: Team[]): Team[] => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return teamsToPaginate.slice(startIndex, endIndex)
  }, [currentPage, pageSize])

  /**
   * Load teams directly from JSON files (much faster than API calls)
   */
  const fetchTeams = async ({ league }: { league?: string } = {}) => {
    try {
      setLoading(true)
      
      // Load teams directly from JSON files in parallel
      const allTeamsData = await loadTeamsFromJSON()
      
      // Apply league filter if specified
      let filteredTeams = allTeamsData
      if (league) {
        filteredTeams = allTeamsData.filter(team => 
          team.league?.toLowerCase() === league.toLowerCase()
        )
      }
      
      console.log("Loaded teams from JSON:", filteredTeams.length, "teams")
      setAllTeams(filteredTeams)
      setTeams(filteredTeams)
      
      // Extract unique sports and leagues for the filter system
      const sportsSet = new Set(filteredTeams.map((team: Team) => team.sport).filter(Boolean))
      const leaguesSet = new Set(filteredTeams.map((team: Team) => team.league).filter(Boolean))
      
      setAvailableSports(Array.from(sportsSet) as string[])
      setAvailableLeagues(Array.from(leaguesSet) as string[])

      setError(null)
    } catch (err) {
      console.error("Error loading teams from JSON:", err)
      setError(err instanceof Error ? err.message : "An error occurred")
    } finally {
      setLoading(false)
    }
  }

  /**
   * Optimized client-side search filtering with pre-computed search strings
   */
  const performSearch = useCallback((query: string, teamsToFilter: Team[]) => {
    if (!query.trim()) {
      return teamsToFilter
    }
    
    const lowercaseQuery = query.toLowerCase().trim()
    
    // For single word searches, use simple includes for speed
    if (!lowercaseQuery.includes(' ')) {
      return teamsToFilter.filter(team => {
        return team.name.toLowerCase().includes(lowercaseQuery) ||
               team.city.toLowerCase().includes(lowercaseQuery) ||
               (team.abbreviation && team.abbreviation.toLowerCase().includes(lowercaseQuery)) ||
               (team.league && team.league.toLowerCase().includes(lowercaseQuery)) ||
               (team.sport && team.sport.toLowerCase().includes(lowercaseQuery))
      })
    }
    
    // For multi-word searches, use term matching
    const queryTerms = lowercaseQuery.split(' ').filter(term => term.length > 0)
    
    return teamsToFilter.filter(team => {
      // Create searchable text once per team
      const searchText = `${team.name} ${team.city} ${team.abbreviation || ''} ${team.league || ''} ${team.sport || ''}`.toLowerCase()
      
      // Check if all query terms are found (AND logic for multi-word searches)
      return queryTerms.every(term => searchText.includes(term))
    })
  }, [])

  // Handle search with reduced debouncing for better performance
  const searchTeams = useCallback((query: string = '') => {
    setSearchQuery(query)
    
    // For short queries, search immediately
    if (query.length <= 2) {
      setSearching(false)
      return
    }
    
    setSearching(true)
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Set new timeout for debounced search - reduced to 150ms for faster response
    searchTimeoutRef.current = window.setTimeout(() => {
      setSearching(false)
    }, 150)
  }, [])
  
  // Immediate search without debouncing for better performance
  const performImmediateSearch = useCallback((query: string, teamsToFilter: Team[]) => {
    if (query.length <= 2) {
      return teamsToFilter
    }
    return performSearch(query, teamsToFilter)
  }, [performSearch])

  // Optimized filtering with sport/league system, sorting, and pagination
  useEffect(() => {
    // Skip processing if no teams are loaded yet
    if (allTeams.length === 0) return
    
    // Immediate filtering for better perceived performance
    let teamsToShow = allTeams
    
    // Apply sport filter if active - most selective filter first
    if (activeSport) {
      teamsToShow = teamsToShow.filter(team => team.sport === activeSport)
    }
    
    // Apply league filters if active (multiple leagues supported)
    if (activeLeagues.length > 0) {
      teamsToShow = teamsToShow.filter(team => activeLeagues.includes(team.league || ''))
    }
    
    // Apply search filter if there's a query (immediate search)
    if (searchQuery && searchQuery.length > 2) {
      teamsToShow = performImmediateSearch(searchQuery, teamsToShow)
    }
    
    // Apply sorting
    const sortedTeams = sortTeams(teamsToShow)
    
    // Update total count for pagination
    setTotalTeams(sortedTeams.length)
    
    // Store all filtered and sorted teams
    setTeams(sortedTeams)
    
    // Apply pagination
    const paginatedTeams = paginateTeams(sortedTeams)
    setPaginatedTeams(paginatedTeams)
    
  }, [allTeams, activeSport, activeLeagues, searchQuery, performImmediateSearch, sortTeams, paginateTeams])

  // Add flag to prevent duplicate fetches
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (!isInitialized) {
      fetchTeams()
      setIsInitialized(true)
    }
  }, [isInitialized])

  /**
   * Filter teams by sport
   */
  const filterBySport = (sport: string | null) => {
    setActiveSport(sport)
    // Reset league filters when sport changes
    if (sport !== activeSport) {
      setActiveLeagues([])
    }
  }
  
  /**
   * Filter teams by leagues (multiple selection)
   */
  const filterByLeagues = (leagues: string[]) => {
    setActiveLeagues(leagues)
  }
  
  /**
   * Get available leagues for the currently selected sport
   */
  const getLeaguesForSport = (sport: string | null): string[] => {
    if (!sport) return availableLeagues
    
    const sportTeams = allTeams.filter(team => team.sport === sport)
    const sportLeagues = new Set(sportTeams.map(team => team.league).filter(Boolean))
    return Array.from(sportLeagues) as string[]
  }

  /**
   * Handle sorting changes
   */
  const handleSort = (newSortBy: SortOption, newDirection?: SortDirection) => {
    // If clicking the same sort option, toggle direction
    if (newSortBy === sortBy && !newDirection) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(newSortBy)
      setSortDirection(newDirection || 'asc')
    }
    // Reset to first page when sorting changes
    setCurrentPage(1)
  }

  /**
   * Handle page size changes
   */
  const handlePageSizeChange = (newPageSize: 25 | 50 | 100) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page
  }

  /**
   * Handle page changes
   */
  const handlePageChange = (newPage: number) => {
    const maxPage = Math.ceil(totalTeams / pageSize)
    if (newPage >= 1 && newPage <= maxPage) {
      setCurrentPage(newPage)
    }
  }

  /**
   * Get pagination info
   */
  const getPaginationInfo = () => {
    const totalPages = Math.ceil(totalTeams / pageSize)
    const startIndex = (currentPage - 1) * pageSize + 1
    const endIndex = Math.min(currentPage * pageSize, totalTeams)
    
    return {
      currentPage,
      totalPages,
      pageSize,
      totalTeams,
      startIndex,
      endIndex,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1
    }
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  return {
    teams: teams, // Return ALL teams (no pagination for now)
    allFilteredTeams: teams, // All filtered teams (useful for counts)
    loading,
    searching,
    error,
    activeSport,
    activeLeagues,
    availableSports,
    availableLeagues,
    searchQuery,
    sortBy,
    sortDirection,
    searchTeams,
    filterBySport,
    filterByLeagues,
    getLeaguesForSport,
    handleSort,
    handlePageSizeChange,
    handlePageChange,
    getPaginationInfo,
    refetch: () => fetchTeams(),
  }
}
